import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, ExternalLink, FileText, Video, Link2, Image, Globe, Lock, Folder, FolderOpen, GripVertical, X, Check, ChevronUp, ChevronDown } from "lucide-react";

const VERTICALS = [
  { value: "all", label: "All Verticals (No Gate)" },
  { value: "BizConnect360", label: "BizConnect360", color: "#374151" },
  { value: "Fundable360", label: "Fundable360", color: "#84ab4a" },
  { value: "Share360", label: "Share360", color: "#1A6FD4" },
  { value: "Legacy360", label: "Legacy360", color: "#C8920A" },
  { value: "Scale360", label: "Scale360", color: "#E8620A" },
  { value: "Media360", label: "Media360", color: "#7B4FD4" },
];

const MATERIAL_TYPES = [
  { value: "pdf", label: "PDF", icon: FileText, color: "#D93025" },
  { value: "video", label: "Video", icon: Video, color: "#1A6FD4" },
  { value: "link", label: "Link", icon: Link2, color: "#0FA8A8" },
  { value: "pptx", label: "PowerPoint", icon: FileText, color: "#D04A02" },
  { value: "image", label: "Image", icon: Image, color: "#7B4FD4" },
  { value: "template", label: "Template", icon: FileText, color: "#E8620A" },
];

const CERT_REQUIRED = ["Fundable360", "Share360", "Legacy360"];

const MODULE_TYPES = [
  { value: "general",    label: "General Resources" },
  { value: "training",   label: "Training Calls" },
  { value: "scripts",    label: "Scripts & Templates" },
  { value: "pricing",    label: "Pricing & Fees" },
  { value: "onboarding", label: "Quick Start / Onboarding" },
];

// ─── Folder Manager Modal ─────────────────────────────────────────────────────
function FolderManagerModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [selectedVertical, setSelectedVertical] = useState("Share360");
  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolder, setEditingFolder] = useState<{
    id: number; name: string; description: string; coverImageUrl: string; moduleType: string;
  } | null>(null);

  const utils = trpc.useUtils();
  const foldersQuery = trpc.marketing.listFolders.useQuery(
    { vertical: selectedVertical },
    { enabled: open }
  );

  const invalidate = () => { utils.marketing.listFolders.invalidate(); };

  const createMutation = trpc.marketing.createFolder.useMutation({
    onSuccess: () => { toast.success("Module created"); setNewFolderName(""); invalidate(); },
    onError: e => toast.error(e.message),
  });
  const updateMutation = trpc.marketing.updateFolder.useMutation({
    onSuccess: () => { toast.success("Module updated"); setEditingFolder(null); invalidate(); },
    onError: e => toast.error(e.message),
  });
  const deleteMutation = trpc.marketing.deleteFolder.useMutation({
    onSuccess: () => { toast.success("Module deleted"); invalidate(); },
    onError: e => toast.error(e.message),
  });
  const reorderMutation = trpc.marketing.reorderFolders.useMutation({
    onSuccess: () => invalidate(),
    onError: e => toast.error(e.message),
  });

  const folders = foldersQuery.data ?? [];

  const moveFolder = (index: number, dir: -1 | 1) => {
    const arr = [...folders];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    reorderMutation.mutate({ items: arr.map((f: any, i) => ({ id: f.id, sortOrder: i })) });
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) { onClose(); setEditingFolder(null); } }}>
      <DialogContent className="max-w-lg flex flex-col max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-amber-500" />
            Manage Modules
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 pt-1 pr-1">
          {/* Vertical picker */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Vertical</label>
            <Select value={selectedVertical} onValueChange={v => { setSelectedVertical(v); setEditingFolder(null); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {VERTICALS.slice(1).map(v => (
                  <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Existing modules */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
              Modules for {selectedVertical} ({folders.length})
            </label>
            {foldersQuery.isLoading ? (
              <div className="text-xs text-gray-400 py-3 text-center">Loading…</div>
            ) : folders.length === 0 ? (
              <div className="text-xs text-gray-400 py-3 text-center border border-dashed border-gray-200 rounded-lg">
                No modules yet — add one below
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {folders.map((f: any, idx: number) => (
                  <div key={f.id} className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
                    {editingFolder !== null && editingFolder.id === f.id ? (
                      /* Expanded edit form */
                      <div className="p-3 space-y-3">
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Module Name *</label>
                          <input
                            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#84ab4a]"
                            value={editingFolder.name}
                            onChange={e => setEditingFolder(ef => ef ? { ...ef, name: e.target.value } : ef)}
                            autoFocus
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Description</label>
                          <Textarea
                            className="text-sm resize-none"
                            rows={2}
                            placeholder="Short description shown to associates…"
                            value={editingFolder.description}
                            onChange={e => setEditingFolder(ef => ef ? { ...ef, description: e.target.value } : ef)}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Cover Image URL</label>
                          <Input
                            placeholder="https://storage.googleapis.com/msgsndr/…"
                            value={editingFolder.coverImageUrl}
                            onChange={e => setEditingFolder(ef => ef ? { ...ef, coverImageUrl: e.target.value } : ef)}
                          />
                          <p className="text-[10px] text-gray-400 mt-1">
                            Upload to Wibiz Media Storage, then paste the URL. Leave blank for a gradient.
                          </p>
                        </div>
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wide block mb-1">Module Type</label>
                          <Select
                            value={editingFolder.moduleType}
                            onValueChange={v => setEditingFolder(ef => ef ? { ...ef, moduleType: v } : ef)}
                          >
                            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {MODULE_TYPES.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <Button
                            className="flex-1 bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0 h-8 text-xs"
                            onClick={() => {
                              if (!editingFolder.name.trim()) return;
                              updateMutation.mutate({
                                id: editingFolder.id,
                                name: editingFolder.name.trim(),
                                description: editingFolder.description.trim() || null,
                                coverImageUrl: editingFolder.coverImageUrl.trim() || null,
                                moduleType: editingFolder.moduleType,
                              });
                            }}
                            disabled={!editingFolder.name.trim() || updateMutation.isPending}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" /> Save
                          </Button>
                          <Button variant="outline" className="h-8 text-xs" onClick={() => setEditingFolder(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Normal row */
                      <div className="flex items-center gap-2 px-3 py-2.5">
                        <Folder className="w-4 h-4 text-amber-400 flex-shrink-0" />
                        <span className="flex-1 text-sm font-medium text-gray-700 truncate">{f.name}</span>
                        {f.moduleType && f.moduleType !== "general" && (
                          <span className="hidden sm:inline text-[9px] font-bold uppercase text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
                            {f.moduleType}
                          </span>
                        )}
                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                          <button
                            onClick={() => moveFolder(idx, -1)}
                            disabled={idx === 0 || reorderMutation.isPending}
                            className="text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors"
                            title="Move up"
                          >
                            <ChevronUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => moveFolder(idx, 1)}
                            disabled={idx === folders.length - 1 || reorderMutation.isPending}
                            className="text-gray-300 hover:text-gray-600 disabled:opacity-20 transition-colors"
                            title="Move down"
                          >
                            <ChevronDown className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <button
                          onClick={() => setEditingFolder({
                            id: f.id,
                            name: f.name,
                            description: f.description ?? "",
                            coverImageUrl: f.coverImageUrl ?? "",
                            moduleType: f.moduleType ?? "general",
                          })}
                          className="text-gray-300 hover:text-[#1A6FD4] transition-colors"
                          title="Edit module details"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete module "${f.name}"? Materials inside will become unmodule.`))
                              deleteMutation.mutate({ id: f.id });
                          }}
                          className="text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add new module */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Add New Module</label>
            <div className="flex gap-2">
              <Input
                placeholder="Module name…"
                value={newFolderName}
                onChange={e => setNewFolderName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && newFolderName.trim())
                    createMutation.mutate({ vertical: selectedVertical, name: newFolderName.trim() });
                }}
              />
              <Button
                className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0 flex-shrink-0"
                onClick={() => {
                  if (newFolderName.trim())
                    createMutation.mutate({ vertical: selectedVertical, name: newFolderName.trim() });
                }}
                disabled={!newFolderName.trim() || createMutation.isPending}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

        </div>
        <div className="flex justify-end pt-3 border-t border-gray-100 mt-1">
          <Button variant="outline" onClick={onClose}>Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export function AdminMarketingMaterials() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showFolderManager, setShowFolderManager] = useState(false);
  const [filterVertical, setFilterVertical] = useState<string>("all");
  const [editTarget, setEditTarget] = useState<{
    id: number;
    title: string;
    description: string;
    url: string;
    type: "pdf" | "video" | "link" | "image" | "template" | "pptx";
    vertical: string;
    folderId: number | null;
  } | null>(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "link" as "pdf" | "video" | "link" | "pptx" | "image" | "template",
    url: "",
    vertical: "all",
    folderId: null as number | null,
  });

  const utils = trpc.useUtils();
  const materialsQuery = trpc.marketing.adminList.useQuery();
  const foldersQuery = trpc.marketing.listFolders.useQuery({});

  const invalidateAll = () => {
    utils.marketing.adminList.invalidate();
    utils.marketing.list.invalidate();
  };

  const addMutation = trpc.marketing.add.useMutation({
    onSuccess: () => {
      toast.success("Material added");
      invalidateAll();
      setShowAddModal(false);
      setForm({ title: "", description: "", type: "link", url: "", vertical: "all", folderId: null });
    },
    onError: e => toast.error(e.message),
  });
  const deleteMutation = trpc.marketing.delete.useMutation({
    onSuccess: () => { toast.success("Material removed"); invalidateAll(); },
    onError: e => toast.error(e.message),
  });
  const updateMutation = trpc.marketing.update.useMutation({
    onSuccess: () => { toast.success("Material updated"); invalidateAll(); setEditTarget(null); },
    onError: e => toast.error(e.message),
  });
  const reorderMaterialsMutation = trpc.marketing.reorderMaterials.useMutation({
    onSuccess: () => invalidateAll(),
    onError: e => toast.error(e.message),
  });
  const reorderFoldersMutation = trpc.marketing.reorderFolders.useMutation({
    onSuccess: () => utils.marketing.listFolders.invalidate(),
    onError: e => toast.error(e.message),
  });

  const handleReorderMaterials = (newItems: any[]) => {
    reorderMaterialsMutation.mutate({
      items: newItems.map((m: any, i: number) => ({ id: m.id, sortOrder: i })),
    });
  };

  const handleMoveFolderInView = (verticalKey: string, index: number, dir: -1 | 1) => {
    const folders = getFoldersForVertical(verticalKey);
    const arr = [...folders];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    reorderFoldersMutation.mutate({ items: arr.map((f: any, i: number) => ({ id: f.id, sortOrder: i })) });
  };

  const materials = materialsQuery.data ?? [];
  const allFolders: any[] = foldersQuery.data ?? [];

  const filtered = filterVertical === "all"
    ? materials
    : materials.filter((m: any) => m.vertical === filterVertical || (!m.vertical && filterVertical === "all"));

  const getTypeInfo = (type: string) => MATERIAL_TYPES.find(t => t.value === type) ?? MATERIAL_TYPES[2];
  const getVerticalInfo = (v: string | null) => VERTICALS.find(vt => vt.value === v) ?? VERTICALS[0];
  const getFoldersForVertical = (v: string) => allFolders.filter((f: any) => f.vertical === v);
  const getFolderName = (folderId: number | null, vertical: string) => {
    if (!folderId) return null;
    return allFolders.find((f: any) => f.id === folderId && f.vertical === vertical)?.name ?? null;
  };

  // Group by vertical
  const byVertical: Record<string, typeof materials> = {};
  for (const m of filtered) {
    const key = (m as any).vertical ?? "all";
    if (!byVertical[key]) byVertical[key] = [];
    byVertical[key].push(m);
  }

  const verticalOrder = ["BizConnect360", "Fundable360", "Share360", "Legacy360", "Scale360", "Media360", "all"];
  const sortedKeys = Object.keys(byVertical).sort((a, b) => {
    const ai = verticalOrder.indexOf(a);
    const bi = verticalOrder.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const handleAdd = () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.url.trim()) return toast.error("URL is required");
    addMutation.mutate({
      title: form.title,
      description: form.description || undefined,
      type: form.type,
      url: form.url,
      vertical: form.vertical === "all" ? undefined : form.vertical,
      folderId: form.folderId ?? undefined,
    });
  };

  const MaterialRow = ({ m, verticalKey, items, index, onReorder }: {
    m: any;
    verticalKey: string;
    items: any[];
    index: number;
    onReorder: (newItems: any[]) => void;
  }) => {
    const typeInfo = getTypeInfo(m.type);
    const TypeIcon = typeInfo.icon;
    const folderName = getFolderName(m.folderId, verticalKey);

    const move = (dir: -1 | 1) => {
      const arr = [...items];
      const target = index + dir;
      if (target < 0 || target >= arr.length) return;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      onReorder(arr);
    };

    return (
      <div className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-all">
        <div className="flex flex-col gap-0.5 flex-shrink-0">
          <button
            onClick={() => move(-1)}
            disabled={index === 0 || reorderMaterialsMutation.isPending}
            className="text-gray-200 hover:text-gray-500 disabled:opacity-20 transition-colors"
            title="Move up"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => move(1)}
            disabled={index === items.length - 1 || reorderMaterialsMutation.isPending}
            className="text-gray-200 hover:text-gray-500 disabled:opacity-20 transition-colors"
            title="Move down"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${typeInfo.color}15` }}>
          <TypeIcon className="w-4 h-4" style={{ color: typeInfo.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-gray-900 truncate">{m.title}</div>
          {m.description && <div className="text-xs text-gray-400 truncate">{m.description}</div>}
          <div className="text-[11px] text-gray-300 font-mono truncate mt-0.5">{m.url}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {folderName && (
            <span className="hidden sm:flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              <Folder className="w-2.5 h-2.5" /> {folderName}
            </span>
          )}
          <Badge variant="outline" className="text-[10px] py-0" style={{ borderColor: `${typeInfo.color}40`, color: typeInfo.color }}>
            {m.type}
          </Badge>
          <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-[#84ab4a] transition-colors">
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <button
            onClick={() => setEditTarget({
              id: m.id, title: m.title, description: m.description ?? "",
              url: m.url, type: m.type, vertical: m.vertical ?? "all",
              folderId: m.folderId ?? null,
            })}
            className="text-gray-300 hover:text-[#1A6FD4] transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => { if (confirm(`Remove "${m.title}"?`)) deleteMutation.mutate({ id: m.id }); }}
            className="text-gray-300 hover:text-red-500 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Marketing Materials</h2>
          <p className="text-sm text-gray-400">Upload and manage collateral per vertical. Agents see materials for their certified verticals only.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="border-gray-200 text-gray-600 hover:bg-gray-50" onClick={() => setShowFolderManager(true)}>
            <FolderOpen className="w-4 h-4 mr-1.5 text-amber-500" /> Manage Modules
          </Button>
          <Button className="bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0" onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-1.5" /> Add Material
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-5">
        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filter:</span>
        <div className="flex gap-2 flex-wrap">
          {[{ value: "all", label: "All" }, ...VERTICALS.slice(1)].map(v => (
            <button
              key={v.value}
              onClick={() => setFilterVertical(v.value)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-all border ${
                filterVertical === v.value
                  ? "bg-[#84ab4a] text-white border-[#84ab4a]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-6 gap-3 mb-6">
        {VERTICALS.slice(1).map(v => {
          const count = materials.filter((m: any) => m.vertical === v.value).length;
          const folderCount = allFolders.filter((f: any) => f.vertical === v.value).length;
          const isCertGated = CERT_REQUIRED.includes(v.value);
          return (
            <div key={v.value} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-center">
              <div className="text-xl font-bold font-mono" style={{ color: v.color }}>{count}</div>
              <div className="text-xs text-gray-500 mt-0.5 font-medium">{v.label}</div>
              {folderCount > 0 && (
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Folder className="w-2.5 h-2.5 text-amber-400" />
                  <span className="text-[9px] text-amber-600 font-semibold">{folderCount} folder{folderCount !== 1 ? "s" : ""}</span>
                </div>
              )}
              {isCertGated && (
                <div className="flex items-center justify-center gap-1 mt-1">
                  <Lock className="w-2.5 h-2.5 text-amber-500" />
                  <span className="text-[9px] text-amber-600 font-semibold">Cert Gated</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Materials grouped by vertical, then by folder */}
      {materialsQuery.isLoading ? (
        <div className="py-12 text-center text-gray-400">Loading materials...</div>
      ) : sortedKeys.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No materials yet</p>
          <p className="text-sm mt-1">Click "Add Material" to upload your first resource.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {sortedKeys.map(verticalKey => {
            const vInfo = getVerticalInfo(verticalKey);
            const isCertGated = CERT_REQUIRED.includes(verticalKey);
            const items = byVertical[verticalKey];
            const verticalFolders = getFoldersForVertical(verticalKey);

            // Group items by folderId
            const inFolder: Record<number, typeof items> = {};
            const unfolderedItems: typeof items = [];
            for (const m of items) {
              const fid = (m as any).folderId;
              if (fid && verticalFolders.find((f: any) => f.id === fid)) {
                if (!inFolder[fid]) inFolder[fid] = [];
                inFolder[fid].push(m);
              } else {
                unfolderedItems.push(m);
              }
            }
            const hasFolders = verticalFolders.length > 0;

            return (
              <div key={verticalKey}>
                {/* Vertical header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full" style={{ background: (vInfo as any).color ?? "#84ab4a" }} />
                  <span className="text-sm font-bold text-gray-700">
                    {verticalKey === "all" ? "All Verticals (No Gate)" : verticalKey}
                  </span>
                  {isCertGated && (
                    <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600 bg-amber-50 py-0">
                      <Lock className="w-2.5 h-2.5 mr-1" /> Cert Required
                    </Badge>
                  )}
                  <span className="text-xs text-gray-400">({items.length} item{items.length !== 1 ? "s" : ""})</span>
                  {hasFolders && <span className="text-xs text-gray-300">· {verticalFolders.length} folder{verticalFolders.length !== 1 ? "s" : ""}</span>}
                </div>

                <div className="space-y-4">
                  {/* Items in folders */}
                  {verticalFolders.map((folder: any, folderIdx: number) => {
                    const folderItems = inFolder[folder.id] ?? [];
                    return (
                      <div key={folder.id}>
                        <div className="flex items-center gap-2 mb-2 pl-1">
                          <div className="flex flex-col gap-0.5 flex-shrink-0">
                            <button
                              onClick={() => handleMoveFolderInView(verticalKey, folderIdx, -1)}
                              disabled={folderIdx === 0 || reorderFoldersMutation.isPending}
                              className="text-gray-200 hover:text-amber-500 disabled:opacity-20 transition-colors"
                              title="Move folder up"
                            >
                              <ChevronUp className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleMoveFolderInView(verticalKey, folderIdx, 1)}
                              disabled={folderIdx === verticalFolders.length - 1 || reorderFoldersMutation.isPending}
                              className="text-gray-200 hover:text-amber-500 disabled:opacity-20 transition-colors"
                              title="Move folder down"
                            >
                              <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                          <Folder className="w-3.5 h-3.5 text-amber-400" />
                          <span className="text-xs font-bold text-amber-700">{folder.name}</span>
                          <span className="text-xs text-gray-300">({folderItems.length})</span>
                        </div>
                        {folderItems.length === 0 ? (
                          <div className="text-xs text-gray-300 pl-5 italic">Empty folder</div>
                        ) : (
                          <div className="grid grid-cols-1 gap-2 pl-5 border-l-2 border-amber-100">
                            {folderItems.map((m: any, idx: number) => (
                              <MaterialRow key={m.id} m={m} verticalKey={verticalKey}
                                items={folderItems} index={idx} onReorder={handleReorderMaterials} />
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Unfoldered items */}
                  {unfolderedItems.length > 0 && (
                    <div>
                      {hasFolders && (
                        <div className="flex items-center gap-2 mb-2 pl-1">
                          <span className="text-xs font-semibold text-gray-400">No folder</span>
                          <span className="text-xs text-gray-300">({unfolderedItems.length})</span>
                        </div>
                      )}
                      <div className={`grid grid-cols-1 gap-2 ${hasFolders ? "pl-5 border-l-2 border-gray-100" : ""}`}>
                        {unfolderedItems.map((m: any, idx: number) => (
                          <MaterialRow key={m.id} m={m} verticalKey={verticalKey}
                            items={unfolderedItems} index={idx} onReorder={handleReorderMaterials} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Folder Manager Modal */}
      <FolderManagerModal open={showFolderManager} onClose={() => setShowFolderManager(false)} />

      {/* Edit Material Modal */}
      <Dialog open={!!editTarget} onOpenChange={open => { if (!open) setEditTarget(null); }}>
        <DialogContent className="max-w-md flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#1A6FD4]" />
              Edit Material
            </DialogTitle>
          </DialogHeader>
          {editTarget && (
            <>
              <div className="flex-1 overflow-y-auto space-y-4 pt-2 pr-1">
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Title *</label>
                  <Input value={editTarget.title} onChange={e => setEditTarget(t => t ? { ...t, title: e.target.value } : t)} />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Description</label>
                  <Textarea value={editTarget.description} onChange={e => setEditTarget(t => t ? { ...t, description: e.target.value } : t)} rows={3} />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">URL / Link *</label>
                  <Input placeholder="https://..." value={editTarget.url} onChange={e => setEditTarget(t => t ? { ...t, url: e.target.value } : t)} />
                  <p className="text-[11px] text-gray-400 mt-1">Upload to Wibiz Media Storage first, then paste the URL here.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Type *</label>
                    <Select value={editTarget.type} onValueChange={v => setEditTarget(t => t ? { ...t, type: v as any } : t)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MATERIAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Vertical</label>
                    <Select value={editTarget.vertical} onValueChange={v => setEditTarget(t => t ? { ...t, vertical: v, folderId: null } : t)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {VERTICALS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Folder select — only for specific verticals */}
                {editTarget.vertical && editTarget.vertical !== "all" && (
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">
                      Folder <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    {getFoldersForVertical(editTarget.vertical).length === 0 ? (
                      <p className="text-xs text-gray-400 italic">No folders for {editTarget.vertical} yet — create them in "Manage Folders".</p>
                    ) : (
                      <Select
                        value={editTarget.folderId?.toString() ?? "none"}
                        onValueChange={v => setEditTarget(t => t ? { ...t, folderId: v === "none" ? null : parseInt(v) } : t)}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— No folder —</SelectItem>
                          {getFoldersForVertical(editTarget.vertical).map((f: any) => (
                            <SelectItem key={f.id} value={f.id.toString()}>{f.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-3 pt-3 border-t border-gray-100 mt-1">
                <Button variant="outline" className="flex-1" onClick={() => setEditTarget(null)}>Cancel</Button>
                <Button
                  className="flex-1 bg-[#1A6FD4] hover:bg-[#1559b0] text-white border-0"
                  onClick={() => updateMutation.mutate({
                    id: editTarget.id,
                    title: editTarget.title,
                    description: editTarget.description || undefined,
                    url: editTarget.url || undefined,
                    type: editTarget.type,
                    vertical: editTarget.vertical === "all" ? null : editTarget.vertical,
                    folderId: editTarget.folderId,
                  })}
                  disabled={!editTarget.title.trim() || !editTarget.url.trim() || updateMutation.isPending}
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Material Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#84ab4a]" />
              Add Marketing Material
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 pt-2 pr-1">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Title *</label>
              <Input
                placeholder="e.g. Fundable360 One-Pager"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Description</label>
              <Textarea
                placeholder="Brief description of this material..."
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Type *</label>
                <Select value={form.type} onValueChange={v => setForm(f => ({ ...f, type: v as any }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MATERIAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">Vertical</label>
                <Select value={form.vertical} onValueChange={v => setForm(f => ({ ...f, vertical: v, folderId: null }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VERTICALS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Folder select */}
            {form.vertical && form.vertical !== "all" && (
              <div>
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">
                  Folder <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                {getFoldersForVertical(form.vertical).length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No folders for {form.vertical} yet — create them via "Manage Folders".</p>
                ) : (
                  <Select
                    value={form.folderId?.toString() ?? "none"}
                    onValueChange={v => setForm(f => ({ ...f, folderId: v === "none" ? null : parseInt(v) }))}
                  >
                    <SelectTrigger><SelectValue placeholder="— No folder —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No folder —</SelectItem>
                      {getFoldersForVertical(form.vertical).map((f: any) => (
                        <SelectItem key={f.id} value={f.id.toString()}>{f.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5 block">URL / Link *</label>
              <Input
                placeholder="https://..."
                value={form.url}
                onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
              />
              <p className="text-[11px] text-gray-400 mt-1">
                For PDFs/videos, upload to Wibiz Media Storage first and paste the URL here.
              </p>
            </div>
            {form.vertical && form.vertical !== "all" && CERT_REQUIRED.includes(form.vertical) && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <Lock className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  This material will be gated behind <strong>{form.vertical}</strong> certification.
                </p>
              </div>
            )}
            {(!form.vertical || form.vertical === "all") && (
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <Globe className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  No vertical selected — visible to all agents regardless of certification.
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-3 pt-3 border-t border-gray-100 mt-1">
            <Button variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button
              className="flex-1 bg-[#84ab4a] hover:bg-[#6e9038] text-white border-0"
              onClick={handleAdd}
              disabled={addMutation.isPending}
            >
              {addMutation.isPending ? "Adding..." : "Add Material"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
